import { useState, useEffect } from 'react'
import { isEmpty } from 'lodash'
import toast from 'react-hot-toast'
import URLParse from 'url-parse'
import mime from 'mime'

import * as uploadService from 'services/upload'
import { IMAGE_UPLOAD_LIMIT_SIZE } from 'utils/constants'
import { trueErrorMessage } from 'utils/error'
import { resizeFile } from 'utils/common'
import {
	UploadedAssetTarget,
	useCreateOrUpdateUploadedAssetMutation,
	useTargetPresignUploadUrlMutation,
} from 'graphql/generated/scheme'

interface UseAvatarUploadProps {
	isReadOnly?: boolean
	uploading?: boolean
	setUploading?: (...args: any) => any
	defaultValue?: string
	onChange?: (url: string) => void
}

export const useAvatarUpload = ({
	isReadOnly,
	uploading,
	setUploading,
	defaultValue = '',
	onChange,
}: UseAvatarUploadProps) => {
	const [images, setImages] = useState<any[]>([])
	const [avatar, setAvatar] = useState<string>(defaultValue)
	const [openAvatarEditorModal, setOpenAvatarEditorModal] = useState<boolean>(false)
	const [fileInput, setFileInput] = useState<any>(null)
	const [fileOutput, setFileOutput] = useState<any>(null)

	const [createOrUpdateUploadedAsset] = useCreateOrUpdateUploadedAssetMutation()
	const [avatarPresignUploadUrl, { loading: presignUploadUrlLoading }] = useTargetPresignUploadUrlMutation({
		onCompleted: data => {
			if (fileOutput) {
				setUploading?.(true)

				const presignUploadUrl = data.uploaded_asset?.presignUploadUrl
				if (!isEmpty(images) && presignUploadUrl) {
					uploadService
						.uploadToPresignedUrl({
							presignedS3Url: presignUploadUrl,
							file: fileOutput,
						})
						.then(() => {
							const parsedUrl = URLParse(presignUploadUrl, true)
							const url = parsedUrl.pathname.slice(1)

							setAvatar(url)
							onChange?.(url)

							createOrUpdateUploadedAsset({
								variables: {
									url: url,
									target: UploadedAssetTarget.EventAvatar,
								},
							}).finally(() => {
								setUploading?.(false)
							})
						})
						.catch(() => {
							toast.error(`Upload failure!`)
							setImages([])
							setUploading?.(false)
						})
				}
			}
		},
		onError: error => {
			toast.error(trueErrorMessage(error))
			setImages([])
			setUploading?.(false)
		},
	})

	useEffect(() => {
		if (defaultValue !== avatar) {
			setAvatar(defaultValue)
		}
	}, [defaultValue])

	useEffect(() => {
		const resizeImage = async () => {
			if (!isEmpty(images)) {
				try {
					const file = await resizeFile(images[0].file)
					setFileInput(file)
					setOpenAvatarEditorModal(true)
				} catch (err) {
					console.log(err)
				}
			}
		}
		resizeImage()
	}, [images])

	const handleImageUpload = (imageList: any) => {
		if (imageList?.length) {
			const { size } = imageList[0].file as File
			if (size > IMAGE_UPLOAD_LIMIT_SIZE * 1024 * 1024) {
				toast.error(`Image support up to ${IMAGE_UPLOAD_LIMIT_SIZE}MB`)
				return
			}
			setImages(imageList)
		}
	}

	const handleAvatarSubmit = (file: any) => {
		const { type, size } = file
		const extension = mime.getExtension(type) as string

		setFileOutput(file)
		avatarPresignUploadUrl({
			variables: {
				contentType: type,
				extension,
				contentLength: size,
				target: UploadedAssetTarget.EventAvatar,
			},
		})
	}

	const handleClear = () => {
		setAvatar('')
		setImages([])
		setFileInput(null)
		setFileOutput(null)
		onChange?.('')
	}

	const isLoading = uploading || presignUploadUrlLoading
	const isDisabled = isLoading || isReadOnly

	return {
		avatar,
		fileInput,
		openAvatarEditorModal,
		setOpenAvatarEditorModal,
		isLoading,
		isDisabled,
		handleImageUpload,
		handleAvatarSubmit,
		handleClear,
	}
}
